/**
 * Concentration Pool Engine - DAoC Style Sustained Buff Management
 * Each paladin / buffer has a 100-point Conc pool that is reserved by active auras/wards.
 */

import { EntityState, ActiveBuff } from '../../core/Types';
import { EventBus } from '../../core/EventBus';

export class ConcentrationEngine {
  /**
   * Get available free concentration points
   */
  public getAvailableConc(caster: EntityState): number {
    return caster.resources.conc;
  }

  /**
   * Try reserving concentration for a new buff. Returns true if successful.
   */
  public tryReserve(
    caster: EntityState,
    target: EntityState,
    buff: ActiveBuff
  ): boolean {
    // Check if target already has this buff from this caster, if so toggle it off
    const existingIdx = target.buffs.findIndex((b) => b.id === buff.id && b.sourceId === caster.id);
    if (existingIdx >= 0) {
      const removed = target.buffs.splice(existingIdx, 1)[0];
      caster.resources.conc = Math.min(caster.resources.maxConc, caster.resources.conc + removed.concCost);
      EventBus.emit('BUFF_REMOVED', {
        targetId: target.id,
        buffId: removed.id,
        concReturned: removed.concCost,
      });
      return false; // Toggled off
    }

    // Check if sufficient concentration is available
    if (caster.resources.conc < buff.concCost) {
      return false; // Insufficient concentration
    }

    // Reserve points & Add buff
    caster.resources.conc -= buff.concCost;
    target.buffs.push(buff);

    EventBus.emit('BUFF_APPLIED', {
      targetId: target.id,
      buff,
    });

    return true; // Activated
  }

  /**
   * Release all buffs maintained by a caster (e.g. upon death)
   */
  public releaseAllByCaster(casterId: string, allEntities: EntityState[]): void {
    for (const entity of allEntities) {
      const remaining: ActiveBuff[] = [];
      for (const buff of entity.buffs) {
        if (buff.sourceId === casterId) {
          EventBus.emit('BUFF_REMOVED', {
            targetId: entity.id,
            buffId: buff.id,
            concReturned: buff.concCost,
          });
        } else {
          remaining.push(buff);
        }
      }
      entity.buffs = remaining;
    }
  }

  /**
   * Remove a specific buff by id
   */
  public removeBuff(target: EntityState, buffId: string, caster?: EntityState): void {
    const idx = target.buffs.findIndex((b) => b.id === buffId);
    if (idx >= 0) {
      const removed = target.buffs.splice(idx, 1)[0];
      if (caster) {
        caster.resources.conc = Math.min(caster.resources.maxConc, caster.resources.conc + removed.concCost);
      }
      EventBus.emit('BUFF_REMOVED', {
        targetId: target.id,
        buffId: removed.id,
        concReturned: removed.concCost,
      });
    }
  }
}

export const Concentration = new ConcentrationEngine();
